/*
  MoM hero switcher  —  assets/mom-hero-switch.js
  <mom-hero>: icon tabs that change the scene, lazy scenes and video, and the scroll progress
  that eases the card back and opens the header seal into the wordmark.
  Ported from the Chummys Bakery hero (assets/chummys-hero.js).
*/
(() => {
  if (customElements.get('mom-hero')) return;

  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const mqDesktop = window.matchMedia('(min-width: 768px)');
  const mqReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const root = document.documentElement;
  const idle = window.requestIdleCallback || ((fn) => window.setTimeout(fn, 1200));

  class MomHero extends HTMLElement {
    constructor() {
      super();
      this.index = 0;
      this.scrolled = false;
      this.hydrated = new Set();
      this.autoplayTimer = null;
      this.labelTimer = null;
      this.raf = null;
      this.paused = false;
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onVisibility = this.onVisibility.bind(this);
    }

    connectedCallback() {
      this.sticky = this.querySelector('.mom-hero__sticky');
      this.frame = this.querySelector('.mom-hero__frame');
      this.panels = Array.from(this.querySelectorAll('.mom-hero__panel'));
      this.tabs = Array.from(this.querySelectorAll('.mom-hero__tab'));
      this.switcher = this.querySelector('.mom-hero__switch');
      this.highlight = this.querySelector('.mom-hero__highlight');
      this.label = this.querySelector('.mom-hero__switch-label');
      root.classList.add('mom-has-hero');

      const firstConnect = !this.bound;
      this.bound = true;
      this.index = clamp(parseInt(this.dataset.default || '0', 10) || 0, 0, Math.max(0, this.panels.length - 1));
      this.hydrated.add(this.index);

      if (firstConnect) {
        this.tabs.forEach((tab) => {
          const i = Number(tab.dataset.index);
          tab.addEventListener('click', () => this.activate(i, { user: true }));
          tab.addEventListener('mouseenter', () => { this.hydrate(i); this.showLabel(i, true); });
          tab.addEventListener('mouseleave', () => this.showLabel(this.index, false));
          tab.addEventListener('focus', () => this.showLabel(i, true));
          tab.addEventListener('blur', () => this.showLabel(this.index, false));
        });
        if (this.switcher) {
          this.switcher.addEventListener('keydown', (e) => this.onKey(e));
          this.switcher.addEventListener('mouseenter', () => { this.paused = true; });
          this.switcher.addEventListener('mouseleave', () => { this.paused = false; });
        }
        this.bindSwipe();
        this.bindDesignMode();
      }

      this.setActiveState(this.index);
      this.positionHighlight(true);
      this.playVideo(this.index);

      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('scroll', this.onScroll, { passive: true });
      document.addEventListener('visibilitychange', this.onVisibility);

      // warm the other scenes once the page has settled, smallest rendition only
      const preload = () => idle(() => this.panels.forEach((_, i) => this.hydrate(i, { cheap: true })));
      if (document.readyState === 'complete') preload();
      else window.addEventListener('load', preload, { once: true });

      this.placeProof();
      window.addEventListener('load', () => { this.placeProof(); this.positionHighlight(true); }, { once: true });
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.positionHighlight(true)).catch(() => {});
      this.update();
      this.startAutoplay();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.stopAutoplay();
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = null;
      root.classList.remove('mom-has-hero', 'mom-scrolled');
      root.style.removeProperty('--mh-progress');
      root.style.removeProperty('--mh-p2');
    }

    activate(i, opts = {}) {
      if (i === this.index) {
        if (opts.user) this.showLabel(i, true, 1200);
        return;
      }
      const prev = this.index;
      this.index = i;
      this.hydrate(i);
      this.setActiveState(i);
      this.positionHighlight();
      this.showLabel(i, true, 1400);
      this.playVideo(i);
      this.pauseVideo(prev);
      if (opts.focus && this.tabs[i]) this.tabs[i].focus({ preventScroll: true });
      if (opts.user) this.startAutoplay();
      this.dispatchEvent(new CustomEvent('mom:hero:change', { bubbles: true, detail: { index: i, label: this.tabs[i] ? this.tabs[i].dataset.label : '' } }));
    }

    next(step = 1, opts = {}) {
      const n = this.panels.length;
      if (!n) return;
      this.activate((this.index + step + n) % n, opts);
    }

    setActiveState(i) {
      this.panels.forEach((panel, k) => {
        const on = k === i;
        panel.classList.toggle('is-active', on);
        panel.setAttribute('aria-hidden', on ? 'false' : 'true');
        if ('inert' in panel) panel.inert = !on;
      });
      this.tabs.forEach((tab, k) => {
        const on = k === i;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
      });
    }

    positionHighlight(immediate = false) {
      const tab = this.tabs[this.index];
      if (!tab || !this.highlight || !this.switcher) return;
      const first = this.tabs[0];
      const x = tab.offsetLeft - first.offsetLeft;
      if (immediate) this.highlight.style.transition = 'none';
      this.highlight.style.width = `${tab.offsetWidth}px`;
      this.highlight.style.height = `${tab.offsetHeight}px`;
      this.highlight.style.transform = `translateX(${x}px)`;
      this.switcher.style.setProperty('--mh-label-x', `${tab.offsetLeft + tab.offsetWidth / 2}px`);
      if (immediate) {
        void this.highlight.offsetHeight;
        this.highlight.style.transition = '';
      }
    }

    showLabel(i, show, autoHide = 0) {
      if (!this.label || !this.switcher) return;
      window.clearTimeout(this.labelTimer);
      const tab = this.tabs[i];
      if (!tab) return;
      this.label.textContent = tab.dataset.label || '';
      this.switcher.style.setProperty('--mh-label-x', `${tab.offsetLeft + tab.offsetWidth / 2}px`);
      this.label.classList.toggle('is-visible', !!show);
      if (show && autoHide) this.labelTimer = window.setTimeout(() => this.label.classList.remove('is-visible'), autoHide);
    }

    onKey(event) {
      const map = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
      if (event.key in map) {
        event.preventDefault();
        this.next(map[event.key], { user: true, focus: true });
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.activate(0, { user: true, focus: true });
      } else if (event.key === 'End') {
        event.preventDefault();
        this.activate(this.panels.length - 1, { user: true, focus: true });
      }
    }

    bindSwipe() {
      if (!this.frame) return;
      let startX = 0;
      let startY = 0;
      let tracking = false;
      this.frame.addEventListener('touchstart', (e) => {
        if (e.touches.length !== 1) return;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        tracking = true;
      }, { passive: true });
      this.frame.addEventListener('touchend', (e) => {
        if (!tracking) return;
        tracking = false;
        const t = e.changedTouches[0];
        const dx = t.clientX - startX;
        const dy = t.clientY - startY;
        if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.4) this.next(dx < 0 ? 1 : -1, { user: true });
      }, { passive: true });
    }

    bindDesignMode() {
      if (!window.Shopify || !window.Shopify.designMode) return;
      this.addEventListener('shopify:block:select', (event) => {
        const panel = event.target.closest('.mom-hero__panel');
        if (panel) this.activate(Number(panel.dataset.index));
      });
    }

    hydrate(i, opts = {}) {
      if (this.hydrated.has(i) || !this.panels[i]) return;
      if (!opts.cheap) this.hydrated.add(i);
      this.panels[i].querySelectorAll('[data-srcset], [data-src]').forEach((el) => {
        if (el.tagName === 'VIDEO' || (el.tagName === 'SOURCE' && el.closest('video'))) return;
        if (opts.cheap) {
          if (el.tagName !== 'IMG' || el.getAttribute('src')) return;
          const smallest = (el.dataset.srcset || '').split(',')[0].trim().split(' ')[0];
          if (smallest) el.setAttribute('src', smallest);
          return;
        }
        if (el.dataset.srcset) { el.srcset = el.dataset.srcset; el.removeAttribute('data-srcset'); }
        if (el.dataset.src) { el.src = el.dataset.src; el.removeAttribute('data-src'); }
      });
    }

    // phones get the mobile cut when the scene has one, otherwise everyone gets the main video
    videoUrls(video) {
      const d = video.dataset;
      const phone = !mqDesktop.matches;
      if (phone && d.srcMobile) return { key: 'mobile', url: d.srcMobile, fallback: d.srcMobileFallback };
      if (d.src) return { key: 'desktop', url: d.src, fallback: d.srcFallback };
      // a scene with only a mobile cut: phones play it, wider screens keep the still image
      return phone && d.srcMobile ? { key: 'mobile', url: d.srcMobile, fallback: d.srcMobileFallback } : null;
    }

    playVideo(i) {
      const video = this.panels[i] && this.panels[i].querySelector('video');
      if (!video || mqReduced.matches || (navigator.connection && navigator.connection.saveData)) return;
      const pick = this.videoUrls(video);
      if (!pick || !pick.url) return;
      if (video.dataset.ready !== pick.key) {
        video.dataset.ready = pick.key;
        video.classList.remove('is-playing');
        video.onerror = () => {
          // the capped rendition is derived from the original's URL; if it is ever missing, use the original
          if (pick.fallback && video.src !== pick.fallback) { video.onerror = null; video.src = pick.fallback; video.load(); video.play().then(() => video.classList.add('is-playing')).catch(() => {}); }
        };
        video.src = pick.url;
        video.load();
      }
      const p = video.play();
      const mark = () => video.classList.add('is-playing');
      if (p && p.then) p.then(mark).catch(() => {});
      else mark();
    }

    pauseVideo(i) {
      const video = this.panels[i] && this.panels[i].querySelector('video');
      if (!video) return;
      video.pause();
      video.classList.remove('is-playing');
    }

    startAutoplay() {
      this.stopAutoplay();
      const seconds = parseFloat(this.dataset.autoplay || '0');
      if (!seconds || mqReduced.matches || this.panels.length < 2) return;
      this.autoplayTimer = window.setInterval(() => {
        if (document.hidden || this.paused || (this.switcher && this.switcher.contains(document.activeElement))) return;
        this.next(1);
      }, seconds * 1000);
    }

    stopAutoplay() {
      if (this.autoplayTimer) window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    onVisibility() {
      if (document.hidden) this.pauseVideo(this.index);
      else this.playVideo(this.index);
    }

    onScroll() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => { this.raf = null; this.update(); });
    }

    onResize() {
      this.positionHighlight(true);
      this.placeProof();
      this.onScroll();
      // rotating a phone or resizing across the breakpoint swaps to the right cut
      this.playVideo(this.index);
    }

    // the review card scales about the card's centre, so it rides the bottom edge as the card eases back
    placeProof() {
      const proof = this.querySelector('.mom-hero__proof');
      if (!proof || !this.frame) return;
      const originFraction = parseFloat(getComputedStyle(root).getPropertyValue('--mh-origin-y')) || 0.5;
      const originY = this.frame.offsetTop + this.frame.offsetHeight * (mqDesktop.matches ? originFraction : 0);
      proof.style.setProperty('--mhp-oy', `${(originY - proof.offsetTop).toFixed(1)}px`);
    }

    update() {
      const y = window.scrollY || root.scrollTop || 0;
      const pinned = mqDesktop.matches && !this.hasAttribute('data-no-pin') && this.sticky;
      let progress = 0;
      if (mqReduced.matches) {
        root.style.setProperty('--mh-progress', '0');
        root.style.setProperty('--mh-p2', '0');
        this.setScrolled(y > 40, y <= 20);
        return;
      }
      if (pinned) {
        const pin = Math.max(1, this.offsetHeight - this.sticky.offsetHeight);
        progress = clamp(-this.getBoundingClientRect().top / pin, 0, 1);
        this.setScrolled(progress >= 0.86, progress <= 0.78);
      } else {
        progress = clamp(y / 260, 0, 1);
        this.setScrolled(y > 36, y <= 14);
      }
      root.style.setProperty('--mh-progress', progress.toFixed(4));
      root.style.setProperty('--mh-p2', Math.min(1, progress * 2).toFixed(4));
    }

    setScrolled(on, off) {
      if (on && !this.scrolled) {
        this.scrolled = true;
        root.classList.add('mom-scrolled');
      } else if (off && this.scrolled) {
        this.scrolled = false;
        root.classList.remove('mom-scrolled');
      }
    }
  }

  customElements.define('mom-hero', MomHero);
})();
